import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiGet } from '../../utils/api';
import Loading from '../Loading';
import { Card } from '../Card';
import { useCurrentOrganization } from 'src/contexts/OrganizationContext';

interface Product {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface Price {
  id: string;
  unit_amount: number;
  currency: string;
  recurring: {
    interval: string;
  };
  type: string;
}

interface ProductData {
  product: Product;
  prices: Price[];
}

interface ProductSelectionProps {
  onProductSelect: (productData: ProductData) => void;
}

export default function ProductSelection({ onProductSelect }: ProductSelectionProps) {
  const [ productData, setProductData ] = useState<ProductData | null>(null);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ error, setError ] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();
  const organization = useCurrentOrganization();
  const org_id = organization.id;

  useEffect(() => {
    if (!isAuthenticated) { return; }
    const fetchProduct = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await apiGet(`/api/${org_id}/billing/product`);
        if (!response.ok) throw new Error('Failed to fetch product details');

        const data = await response.json();
        setProductData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [ isAuthenticated, org_id ]);

  if (isLoading) return (<Loading message="Loading Product Details..." />);

  if (error) {
    return (
      <Card className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <div className="bg-red-100 rounded-lg p-2 flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-red-900 mb-2">Error Loading Product</h3>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (!productData) {
    return (
      <Card className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <div className="bg-yellow-100 rounded-lg p-2 flex-shrink-0">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-yellow-900 mb-2">No Product Available</h3>
              <p className="text-yellow-700">No active products found for subscription.</p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  const { product, prices } = productData;
  const monthlyPrice = prices.find(p => p.recurring?.interval === 'month');

  return (
    <Card className="p-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-4">Billing & Subscriptions</h2>
        <p className="text-lg text-gray-600">Choose your subscription plan and manage your billing preferences</p>
      </div>

      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Available Plans</h2>
          <p className="text-gray-600 text-center mb-8">Choose the subscription plan that best fits your need</p>

          <div className="bg-gradient-to-r from-brand-50 to-indigo-50 rounded-xl p-6 border border-brand-200 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-brand-900">{product.name}</h3>
                {product.description && (
                  <p className="text-brand-700 mt-1">{product.description}</p>
                )}
              </div>
              <div className="flex items-center">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  product.active ? 'bg-brand-100 text-brand-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {product.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {monthlyPrice && (
              <div className="bg-white/50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-brand-600 font-medium mb-1">Monthly Subscription</p>
                    <p className="text-2xl font-bold text-brand-900">
                      ${(monthlyPrice.unit_amount / 100).toFixed(2)} {monthlyPrice.currency.toUpperCase()}
                    </p>
                    <p className="text-sm text-brand-600">per month</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-brand-600 font-medium mb-1">Product ID</p>
                    <p className="text-xs font-mono text-brand-800">{product.id}</p>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => onProductSelect(productData)}
              className="w-full bg-brand-600 hover:bg-brand-700 text-white font-medium py-3 px-6 rounded-md transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              Subscribe to {product.name}
            </button>
          </div>

          <div className="text-center text-sm text-gray-500 mt-6">
            <p>This is a test product. No real charges will be made.</p>
          </div>
        </div>
      </div>
    </Card>
  );
}
